import { BaseScreenProps } from '@/features/quiz/types'
import QuizLogoHeader from '@/features/quiz/quiz-logo-header'
import React from 'react'
import QuizQuestionContainer from '@/features/quiz/quiz-question-container'
import { InferType, object, string } from 'yup'
import {
  FormProvider,
  useController,
  useForm,
  useFormContext,
} from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import RoundedButton from '@/shared/components/buttons/rounded-button'
import { formatDate, parseDate } from '@/lib/utils'
import { differenceInYears, isValid as isValidDate } from 'date-fns'
import { GenericQuizScreenId } from '@/lib/apis/types'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react'
import { CalendarDaysIcon } from '@heroicons/react/20/solid'
import clsx from 'clsx'
import DatePickerCalendar from '@/shared/components/DatePickerCalendar'
import { OLDEST_ALLOWED_AGE, YOUNGEST_ALLOWED_AGE } from '@/lib/constants'

type Props = {
  screenId: GenericQuizScreenId
} & Omit<BaseScreenProps, 'screenId'>

const DateOfBirthPattern = new RegExp('^[0-9]{2}-[0-9]{2}-[0-9]{4}$')
const schema = object({
  dateOfBirth: string()
    .required('Please enter your date of birth.')
    .test('date-of-birth-test', (value, context) => {
      if (!DateOfBirthPattern.test(value))
        return context.createError({
          message: 'Please enter the correct date of birth.',
        })

      const parsed = parseDate(value, 'dd-MM-yyyy')
      if (isNaN(parsed.getDate())) {
        return context.createError({
          message: 'Please enter the correct date of birth.',
        })
      }

      if (differenceInYears(new Date(), parsed) < 18) {
        return context.createError({
          message: 'Must be at least 18 years old.',
        })
      }

      return true
    }),
})

interface FormValue extends InferType<typeof schema> {}

function DateOfBirthPicker() {
  const { control, setValue, formState } = useFormContext<FormValue>()
  const { field, fieldState } = useController({ name: 'dateOfBirth', control })
  const parsedDate = field.value ? parseDate(field.value, 'dd-MM-yyyy') : null
  const selectedDate =
    parsedDate && isValidDate(parsedDate) ? parsedDate : undefined
  const calendarValue = selectedDate
    ? {
        year: selectedDate.getFullYear(),
        month: selectedDate.getMonth() + 1,
        day: selectedDate.getDate(),
      }
    : undefined
  const error = fieldState.isTouched ? fieldState.error?.message : undefined
  const currentYear = new Date().getFullYear()

  return (
    <div>
      <Popover>
        {({ close }) => (
          <>
            <PopoverButton
              id={field.name}
              ref={field.ref}
              type="button"
              aria-label="Date of birth"
              aria-invalid={Boolean(error)}
              aria-describedby={error ? `${field.name}-error` : undefined}
              disabled={formState.isSubmitting}
              className={clsx(
                'flex h-[4.5rem] w-full items-center justify-between rounded-2xl bg-white px-4 text-left text-gray-900 ring-2 ring-inset focus:outline-none focus:ring-blue-600 disabled:bg-gray-300',
                error ? 'ring-red-600' : 'ring-gray-300',
              )}
            >
              <span className="flex flex-col gap-1">
                <span className="text-xs text-gray-500">Date of birth</span>
                <span className="text-base">
                  {selectedDate
                    ? formatDate(selectedDate, 'd MMMM yyyy')
                    : 'Pick a date'}
                </span>
              </span>
              <CalendarDaysIcon
                aria-hidden="true"
                className="h-5 w-5 text-gray-400"
              />
            </PopoverButton>
            <PopoverPanel
              portal
              anchor={{ to: 'bottom start', gap: '8px', padding: '12px' }}
              className="z-[110] rounded-[20px] shadow-[0_24px_60px_rgba(11,11,15,0.14)]"
            >
              <DatePickerCalendar
                className="!shadow-none"
                value={calendarValue}
                startYear={currentYear - OLDEST_ALLOWED_AGE}
                endYear={currentYear - YOUNGEST_ALLOWED_AGE}
                initialView="year"
                onChange={({ year, month, day }) => {
                  setValue(
                    field.name,
                    formatDate(new Date(year, month - 1, day), 'dd-MM-yyyy'),
                    {
                      shouldDirty: true,
                      shouldTouch: true,
                      shouldValidate: true,
                    },
                  )
                  close()
                }}
              />
            </PopoverPanel>
          </>
        )}
      </Popover>
      {error && (
        <p
          id={`${field.name}-error`}
          role="alert"
          className="mt-2 pl-4 text-sm text-red-600"
        >
          {error}
        </p>
      )}
    </div>
  )
}

export default function DateOfBirthScreen({
  screenId,
  answers,
  onChange,
  profile,
}: Props) {
  const answer = answers[screenId]

  let dateOfBirth = ''

  // We always use the date of birth filled in the form assuming this is the
  // updated date of birth. If not, and if the user has a date of birth
  // pre-filled before, we use that.
  if (answer?.type === 'text' && answer.answer.length > 0) {
    dateOfBirth = answer.answer
  } else if (profile?.dob && profile.dob.length > 0) {
    dateOfBirth = formatDate(parseDate(profile.dob), 'dd-MM-yyyy')
  }

  const methods = useForm({
    reValidateMode: 'onChange',
    criteriaMode: 'firstError',
    mode: 'onTouched',
    defaultValues: {
      dateOfBirth: dateOfBirth,
    },
    resolver: yupResolver(schema),
  })

  const { handleSubmit, formState } = methods
  const { isValid, isSubmitting } = formState

  async function onSubmit(values: FormValue) {
    await onChange(
      screenId,
      { id: screenId, type: 'text', answer: values.dateOfBirth },
      true,
    )
  }

  return (
    <>
      <QuizLogoHeader />
      <QuizQuestionContainer
        header={{
          title: 'Let us know your age',
          subtitle: 'Enter your date of birth.',
        }}
      >
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <DateOfBirthPicker />
            <RoundedButton
              disabled={!isValid}
              type="submit"
              className="-mb-1.5 mt-48 sm:mt-64"
              loading={isSubmitting}
            >
              Continue
            </RoundedButton>
          </form>
        </FormProvider>
      </QuizQuestionContainer>
    </>
  )
}
